import { Provider } from '@angular/core';
import { USER_MANAGEMENT_API_CONFIG, UserManagementConfig } from '../config';

/**
 * Provides the User Management Library configuration and services
 * 
 * @param config - Configuration for the User Management API
 * @returns Array of providers to include in your application config
 * 
 * @remarks
 * This is the recommended way to configure the User Management Library.
 * Include this in your application's providers array.
 * 
 * **Authentication:**
 * This library assumes the consuming application handles authentication
 * via HTTP interceptors. Ensure you have an interceptor that adds
 * Bearer tokens to outgoing requests.
 * 
 * @example
 * ```typescript
 * // app.config.ts
 * import { ApplicationConfig } from '@angular/core';
 * import { provideUserManagementLib } from 'user-management-lib';
 * import { environment } from './environments/environment';
 * 
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideHttpClient(withInterceptors([authInterceptor])),
 *     provideUserManagementLib({
 *       apiBaseUrl: environment.apiBaseUrl,
 *       debug: !environment.production
 *     })
 *   ]
 * };
 * ```
 * 
 * @example
 * ```typescript
 * // Using in a component
 * import { Component, inject } from '@angular/core';
 * import { UserService } from 'user-management-lib';
 * 
 * @Component({
 *   selector: 'app-profile',
 *   template: `...`
 * })
 * export class ProfileComponent {
 *   private userService = inject(UserService);
 * 
 *   ngOnInit() {
 *     this.userService.getCurrentUser().subscribe(response => {
 *       console.log(response.data.user);
 *     });
 *   }
 * }
 * ```
 */
export function provideUserManagementLib(config: UserManagementConfig): Provider[] {
  return [
    {
      provide: USER_MANAGEMENT_API_CONFIG,
      useValue: config
    }
  ];
}
